import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import useUrlValidation from '../hooks/useUrlValidation';
import { trackEvent } from '../config/firebase';
import { useAuth } from './AuthContext';
import { saveQRCode, getUserQRCodes, deleteQRCode } from '../config/firebase';

// Initial state
const initialState = {
  // Wizard state
  currentStep: 1,

  // Content state
  contentType: 'url',
  formData: {
    url: '',
    text: '',
    contact: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      organization: '',
    },
  },

  // QR Options state
  qrOptions: {
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    errorCorrectionLevel: 'M',
    includeMargin: true,
    cornerRadius: 0,
    logoFile: null,
    logoSize: 20,
    template: 'basic',
  },

  // History state
  history: [],

  // UI state
  isGenerating: false,
  error: null,

  // Preview state
  previewData: null,
  qrInstance: null,
};

// Action types
const QR_ACTIONS = {
  // Step management
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  NEXT_STEP: 'NEXT_STEP',
  PREVIOUS_STEP: 'PREVIOUS_STEP',

  // Content management
  SET_CONTENT_TYPE: 'SET_CONTENT_TYPE',
  UPDATE_FORM_DATA: 'UPDATE_FORM_DATA',
  SET_URL: 'SET_URL',
  SET_TEXT: 'SET_TEXT',
  UPDATE_CONTACT: 'UPDATE_CONTACT',
  SET_CONTACT_FIELD: 'SET_CONTACT_FIELD',

  // QR Options
  UPDATE_QR_OPTIONS: 'UPDATE_QR_OPTIONS',
  SET_FOREGROUND_COLOR: 'SET_FOREGROUND_COLOR',
  SET_BACKGROUND_COLOR: 'SET_BACKGROUND_COLOR',
  SET_ERROR_CORRECTION: 'SET_ERROR_CORRECTION',
  SET_INCLUDE_MARGIN: 'SET_INCLUDE_MARGIN',
  SET_CORNER_RADIUS: 'SET_CORNER_RADIUS',
  SET_LOGO_FILE: 'SET_LOGO_FILE',
  SET_LOGO_SIZE: 'SET_LOGO_SIZE',
  SET_TEMPLATE: 'SET_TEMPLATE',

  // History
  ADD_TO_HISTORY: 'ADD_TO_HISTORY',
  REMOVE_FROM_HISTORY: 'REMOVE_FROM_HISTORY',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
  LOAD_HISTORY: 'LOAD_HISTORY',

  // UI State
  SET_GENERATING: 'SET_GENERATING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',

  // Preview
  SET_PREVIEW_DATA: 'SET_PREVIEW_DATA',
  SET_QR_INSTANCE: 'SET_QR_INSTANCE',

  // Reset
  RESET_ALL: 'RESET_ALL',
  RESET_FORM: 'RESET_FORM',
  RESET_OPTIONS: 'RESET_OPTIONS',
};

// Reducer function
const qrReducer = (state, action) => {
  switch (action.type) {
    case QR_ACTIONS.SET_CURRENT_STEP:
      return { ...state, currentStep: action.payload };

    case QR_ACTIONS.NEXT_STEP:
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, 3),
      };

    case QR_ACTIONS.PREVIOUS_STEP:
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 1),
      };

    case QR_ACTIONS.SET_CONTENT_TYPE:
      return { ...state, contentType: action.payload };

    case QR_ACTIONS.UPDATE_FORM_DATA:
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };

    case QR_ACTIONS.SET_URL:
      return {
        ...state,
        formData: { ...state.formData, url: action.payload },
      };

    case QR_ACTIONS.SET_TEXT:
      return {
        ...state,
        formData: { ...state.formData, text: action.payload },
      };

    case QR_ACTIONS.UPDATE_CONTACT:
      return {
        ...state,
        formData: {
          ...state.formData,
          contact: { ...state.formData.contact, ...action.payload },
        },
      };

    case QR_ACTIONS.SET_CONTACT_FIELD:
      return {
        ...state,
        formData: {
          ...state.formData,
          contact: {
            ...state.formData.contact,
            [action.payload.field]: action.payload.value,
          },
        },
      };

    case QR_ACTIONS.UPDATE_QR_OPTIONS:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, ...action.payload },
      };

    case QR_ACTIONS.SET_FOREGROUND_COLOR:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, foregroundColor: action.payload },
      };

    case QR_ACTIONS.SET_BACKGROUND_COLOR:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, backgroundColor: action.payload },
      };

    case QR_ACTIONS.SET_ERROR_CORRECTION:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, errorCorrectionLevel: action.payload },
      };

    case QR_ACTIONS.SET_INCLUDE_MARGIN:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, includeMargin: action.payload },
      };

    case QR_ACTIONS.SET_CORNER_RADIUS:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, cornerRadius: action.payload },
      };

    case QR_ACTIONS.SET_LOGO_FILE:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, logoFile: action.payload },
      };

    case QR_ACTIONS.SET_LOGO_SIZE:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, logoSize: action.payload },
      };

    case QR_ACTIONS.SET_TEMPLATE:
      return {
        ...state,
        qrOptions: { ...state.qrOptions, template: action.payload },
      };

    case QR_ACTIONS.ADD_TO_HISTORY: {
      const newHistory = [action.payload, ...state.history.slice(0, 9)]; // Keep last 10
      return { ...state, history: newHistory };
    }

    case QR_ACTIONS.REMOVE_FROM_HISTORY:
      return {
        ...state,
        history: state.history.filter((item) => item.id !== action.payload),
      };

    case QR_ACTIONS.CLEAR_HISTORY:
      return { ...state, history: [] };

    case QR_ACTIONS.LOAD_HISTORY:
      return { ...state, history: action.payload };

    case QR_ACTIONS.SET_GENERATING:
      return { ...state, isGenerating: action.payload };

    case QR_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case QR_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case QR_ACTIONS.SET_PREVIEW_DATA:
      return { ...state, previewData: action.payload };

    case QR_ACTIONS.SET_QR_INSTANCE:
      return { ...state, qrInstance: action.payload };

    case QR_ACTIONS.RESET_FORM:
      return {
        ...state,
        formData: initialState.formData,
        contentType: 'url',
      };

    case QR_ACTIONS.RESET_OPTIONS:
      return {
        ...state,
        qrOptions: initialState.qrOptions,
      };

    case QR_ACTIONS.RESET_ALL:
      return {
        ...initialState,
        history: state.history, // Keep history
      };

    default:
      return state;
  }
};

// Create context
const QRContext = createContext();

// Custom hook to use QR context
export const useQRContext = () => {
  const context = useContext(QRContext);
  if (!context) {
    throw new Error('useQRContext must be used within a QRProvider');
  }
  return context;
};

// Provider component
export const QRProvider = ({ children }) => {
  const [state, dispatch] = useReducer(qrReducer, initialState);
  const previewRef = useRef(null);
  const { user } = useAuth();

  // Use the centralized URL validation hook
  const { isValidUrl } = useUrlValidation();

  // Memoize actions to prevent unnecessary re-renders
  const actions = useMemo(
    () => ({
      // Step management
      setCurrentStep: (step) => {
        dispatch({ type: QR_ACTIONS.SET_CURRENT_STEP, payload: step });
      },

      nextStep: () => {
        // Track QR generation event when moving from content step (1) to customize step (2)
        if (state.currentStep === 1) {
          trackEvent('qr_generated', {
            contentType: state.contentType,
            template: state.qrOptions.template,
            hasLogo: !!state.qrOptions.logoFile,
          });
        }
        dispatch({ type: QR_ACTIONS.NEXT_STEP });
      },

      previousStep: () => {
        dispatch({ type: QR_ACTIONS.PREVIOUS_STEP });
      },

      // Content management
      setContentType: (type) => {
        dispatch({ type: QR_ACTIONS.SET_CONTENT_TYPE, payload: type });
      },

      updateFormData: (data) => {
        dispatch({ type: QR_ACTIONS.UPDATE_FORM_DATA, payload: data });
      },

      setUrl: (url) => {
        dispatch({ type: QR_ACTIONS.SET_URL, payload: url });
      },

      setText: (text) => {
        dispatch({ type: QR_ACTIONS.SET_TEXT, payload: text });
      },

      updateContact: (contactData) => {
        dispatch({ type: QR_ACTIONS.UPDATE_CONTACT, payload: contactData });
      },

      setContactField: (field, value) => {
        dispatch({
          type: QR_ACTIONS.SET_CONTACT_FIELD,
          payload: { field, value },
        });
      },

      // QR Options
      updateQROptions: (options) => {
        dispatch({ type: QR_ACTIONS.UPDATE_QR_OPTIONS, payload: options });
      },

      setForegroundColor: (color) => {
        dispatch({ type: QR_ACTIONS.SET_FOREGROUND_COLOR, payload: color });
      },

      setBackgroundColor: (color) => {
        dispatch({ type: QR_ACTIONS.SET_BACKGROUND_COLOR, payload: color });
      },

      setErrorCorrection: (level) => {
        dispatch({ type: QR_ACTIONS.SET_ERROR_CORRECTION, payload: level });
      },

      setIncludeMargin: (include) => {
        dispatch({ type: QR_ACTIONS.SET_INCLUDE_MARGIN, payload: include });
      },

      setCornerRadius: (radius) => {
        dispatch({ type: QR_ACTIONS.SET_CORNER_RADIUS, payload: radius });
      },

      setLogoFile: (file) => {
        dispatch({ type: QR_ACTIONS.SET_LOGO_FILE, payload: file });
      },

      setLogoSize: (size) => {
        dispatch({ type: QR_ACTIONS.SET_LOGO_SIZE, payload: size });
      },

      setTemplate: (template) => {
        dispatch({ type: QR_ACTIONS.SET_TEMPLATE, payload: template });
      },

      // History management
      addToHistory: async (item) => {
        dispatch({ type: QR_ACTIONS.ADD_TO_HISTORY, payload: item });
        if (user) {
          try {
            await saveQRCode(user.uid, item);
          } catch (error) {
            console.error('Failed to save QR to Firestore:', error);
          }
        } else {
          try {
            const currentHistory = JSON.parse(
              localStorage.getItem('qrHistory') || '[]',
            );
            const newHistory = [item, ...currentHistory.slice(0, 9)];
            localStorage.setItem('qrHistory', JSON.stringify(newHistory));
          } catch (error) {
            console.error('Failed to save to localStorage:', error);
          }
        }
      },

      removeFromHistory: async (id) => {
        dispatch({ type: QR_ACTIONS.REMOVE_FROM_HISTORY, payload: id });
        if (user) {
          try {
            await deleteQRCode(user.uid, id);
          } catch (error) {
            console.error('Failed to delete QR from Firestore:', error);
          }
        } else {
          try {
            const currentHistory = JSON.parse(
              localStorage.getItem('qrHistory') || '[]',
            );
            const updatedHistory = currentHistory.filter(
              (item) => item.id !== id,
            );
            localStorage.setItem('qrHistory', JSON.stringify(updatedHistory));
          } catch (error) {
            console.error('Failed to update localStorage:', error);
          }
        }
      },

      clearHistory: async () => {
        dispatch({ type: QR_ACTIONS.CLEAR_HISTORY });
        if (user) {
          try {
            const codes = await getUserQRCodes(user.uid, 200);
            await Promise.all(codes.map((c) => deleteQRCode(user.uid, c.id)));
          } catch (error) {
            console.error('Failed to clear Firestore history:', error);
          }
        } else {
          localStorage.removeItem('qrHistory');
        }
      },

      loadHistory: () => {
        // This is now handled by the useEffect hook
        console.warn(
          'loadHistory is deprecated. History is now loaded automatically.',
        );
      },

      // UI State
      setGenerating: (isGenerating) => {
        dispatch({ type: QR_ACTIONS.SET_GENERATING, payload: isGenerating });
      },

      setError: (error) => {
        dispatch({ type: QR_ACTIONS.SET_ERROR, payload: error });
      },

      clearError: () => {
        dispatch({ type: QR_ACTIONS.CLEAR_ERROR });
      },

      // Preview
      setPreviewData: (data) => {
        dispatch({ type: QR_ACTIONS.SET_PREVIEW_DATA, payload: data });
      },

      setQRInstance: (instance) => {
        dispatch({ type: QR_ACTIONS.SET_QR_INSTANCE, payload: instance });
      },

      // Reset functions
      resetAll: () => {
        dispatch({ type: QR_ACTIONS.RESET_ALL });
      },

      resetForm: () => {
        dispatch({ type: QR_ACTIONS.RESET_FORM });
      },

      resetOptions: () => {
        dispatch({ type: QR_ACTIONS.RESET_OPTIONS });
      },
    }),
    [
      state.contentType,
      state.currentStep,
      state.qrOptions.logoFile,
      state.qrOptions.template,
      user,
    ],
  ); // Include state dependencies used in nextStep action

  // Memoize utils to prevent unnecessary re-renders
  const utils = useMemo(
    () => ({
      canProceed: () => {
        const { contentType, formData } = state;

        switch (contentType) {
          case 'url':
            return formData.url.trim() !== '' && isValidUrl(formData.url);
          case 'text':
            return formData.text.trim() !== '';
          case 'contact':
            return (
              formData.contact.firstName.trim() !== '' ||
              formData.contact.lastName.trim() !== '' ||
              formData.contact.email.trim() !== '' ||
              formData.contact.phone.trim() !== ''
            );
          default:
            return false;
        }
      },

      getContentData: () => {
        const { contentType, formData } = state;

        switch (contentType) {
          case 'url':
            return formData.url;
          case 'text':
            return formData.text;
          case 'contact': {
            const { firstName, lastName, email, phone, organization } =
              formData.contact;

            // Generate vCard format for contact
            if (firstName || lastName || email || phone) {
              return `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
ORG:${organization || ''}
EMAIL:${email}
TEL:${phone}
END:VCARD`;
            }
            return '';
          }
          default:
            return '';
        }
      },

      getCurrentStepName: () => {
        switch (state.currentStep) {
          case 1:
            return 'content';
          case 2:
            return 'customize';
          case 3:
            return 'download';
          default:
            return 'content';
        }
      },
    }),
    [state, isValidUrl],
  ); // Add dependencies here

  // Load history — Firestore when authenticated, localStorage when anonymous
  useEffect(() => {
    const loadHistory = async () => {
      if (user) {
        // Migrate any existing localStorage QRs to Firestore on first sign-in
        try {
          const localHistory = JSON.parse(
            localStorage.getItem('qrHistory') || '[]',
          );
          if (localHistory.length > 0) {
            for (const item of localHistory) {
              await saveQRCode(user.uid, item);
            }
            localStorage.removeItem('qrHistory');
          }
        } catch (_e) {
          // Migration errors are non-fatal; proceed to load from Firestore
        }

        // Load from Firestore
        const firestoreHistory = await getUserQRCodes(user.uid, 50);
        dispatch({ type: QR_ACTIONS.LOAD_HISTORY, payload: firestoreHistory });
      } else {
        // Anonymous user — load from localStorage
        try {
          const savedHistory = JSON.parse(
            localStorage.getItem('qrHistory') || '[]',
          );
          dispatch({ type: QR_ACTIONS.LOAD_HISTORY, payload: savedHistory });
        } catch (error) {
          console.error('Failed to load history from localStorage:', error);
        }
      }
    };

    loadHistory();
  }, [user]); // Re-run when auth state changes

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      // State
      ...state,

      // Actions
      ...actions,

      // Utils
      ...utils,

      // Refs
      previewRef,

      // Constants
      QR_ACTIONS,
    }),
    [state, actions, utils, previewRef],
  );

  return (
    <QRContext.Provider value={contextValue}>{children}</QRContext.Provider>
  );
};

// Default export
export default QRProvider;
